import { Report } from '@carbon/icons-react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router'

import { latestCampaignQueryOptions } from '@/api/latestCampaign';
import { listReportQueryOptions } from '@/api/listReport';
import { useAuthStore } from '@/auth/store';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { Button } from '@/components/input/button';
import { isNotFoundError } from '@/util/httpError';
import { toReportLinks } from '@/util/reportLinks';

export const Route = createFileRoute('/dashboard/_authenticated/report/')({
  component: RouteComponent,
})

/**
 * The participant's published reports (spec 012 US4). Only campaign names/dates are used:
 * never `campaign.evaluationList`, which holds every evaluation of the campaign.
 */
function RouteComponent() {
  const { userId } = useAuthStore();
  const latestCampaign = useQuery(latestCampaignQueryOptions(userId!));
  const latestCampaignId = latestCampaign.data?.latestCampaignId;
  const reports = useQuery(listReportQueryOptions(userId!, latestCampaignId));

  // A 404 on the latest campaign means there is no campaign yet: nothing to report, not a failure.
  const noCampaign = isNotFoundError(latestCampaign.error);
  const failed = !noCampaign && ((latestCampaign.isError && !latestCampaign.data) || (reports.isError && !reports.data));
  const isLoading = !noCampaign && !failed && !reports.data;
  const links = toReportLinks(reports.data?.campaigns);

  const retry = () => {
    if (latestCampaignId === undefined) {
      void latestCampaign.refetch();
    } else {
      void reports.refetch();
    }
  };

  return (
    <>
      <Breadcrumbs />
      <div className="flex flex-col gap-8">
        <h1 className="
          border-b-2 border-b-primary-100 pb-6 text-page-title text-primary-700
        ">My reports</h1>
        {failed && (
          <div className="flex flex-col items-start gap-3">
            <div className="text-body2 text-error-300">Couldn't load your reports</div>
            <Button onClick={retry} size="sm" type="button">Retry</Button>
          </div>
        )}
        {isLoading && (
          <div aria-busy="true" aria-label="Loading your reports" className="
            grid-cards gap-4
          ">
            {[0, 1, 2].map((key) => (
              <div className="h-20 animate-pulse rounded-lg bg-neutral-100" key={key} />
            ))}
          </div>
        )}
        {!failed && !isLoading && links.length === 0 && (
          <div className="text-body2 text-neutral-600">No reports available yet</div>
        )}
        {links.length > 0 && (
          <ul className="grid-cards gap-4">
            {links.map((link) => (
              <li key={link.campaignId}>
                <Link
                  className="
                    flex min-h-11 items-center gap-4 rounded-lg border-2
                    border-primary-100 p-5
                    hover:bg-primary-50
                  "
                  to={link.target}
                >
                  <Report className="size-7 shrink-0 text-primary-default" />
                  <div className="flex min-w-0 flex-col">
                    <span className="
                      truncate text-body2 font-semibold text-primary-700
                    " title={link.label}>
                      {link.label}
                    </span>
                    <span className="text-body3 text-neutral-600">{link.period}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}
